import { LightbulbIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  factorKey,
  recommendationKey,
  type Conclusion,
  type Recommendation,
} from '@entities/conclusion';
import { Badge, Button, Modal } from '@shared/ui';

import styles from './RecommendationBanner.module.css';

/** Заголовок модалки «Как рассчитано» — цель `aria-labelledby` диалога. */
const MODAL_TITLE_ID = 'conclusion-how-calculated-title';

interface RecommendationBannerProps {
  recommendation: Recommendation;
  /** Заключение рекомендованного варианта — источник разбора расчёта. */
  conclusion: Conclusion | null;
  /** Базовые веса вида: показываются в модалке «Как рассчитано». */
  weights: Record<string, number>;
  /** Подпись автомобиля: марка и модель. Строится вызывающим экраном. */
  vehicleLabel: string;
  algorithmVersion: string;
}

/**
 * «Karl рекомендует» — ранжирование с обоснованием (FE-250, MVP_PRD §38.1).
 *
 * Четыре правила блока, ни одно из которых не косметическое:
 *
 * * **это не вердикт о качестве автомобиля.** Формулировка называет причину
 *   ранга — «лучшая цена относительно медианы при полных ответах автохауса», —
 *   и не говорит, что автомобиль хороший, продавец надёжен, а сделка
 *   безопасна: таких выводов CarViQ.DE не делает (§2);
 * * **рекомендация ничего не блокирует.** Пользователь вправе взять в торг
 *   любой автомобиль четвёрки; блок не отключает кнопок и не прячет остальные
 *   варианты (§38.1);
 * * **разбор расчёта открывается целиком.** Модалка «Как рассчитано» показывает
 *   все компоненты с весами и основаниями, включая **неприменимые**: «судить не
 *   по чему» и «посчитали, вышло плохо» — разные утверждения;
 * * **числа приходят с сервера.** Веса, баллы и отрыв от следующего варианта не
 *   вычисляются на фронте: ранжирование считает backend, и второе его изложение
 *   здесь разошлось бы с первым.
 *
 * Бейдж «Karl empfiehlt» — **синий акцент, а не зелёный** (редизайн 2026-10,
 * DESIGN_SYSTEM §2.3, §2.6): зелёный значит только «соответствует», а
 * рекомендация — выбор Karl и подсказка, а не утверждение о данных. Иконка —
 * лампочка «подсказка», а не галочка: галочка читалась бы как «проверено».
 */
export function RecommendationBanner({
  recommendation,
  conclusion,
  weights,
  vehicleLabel,
  algorithmVersion,
}: RecommendationBannerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const factors = (recommendation.params.factors ?? [])
    .map((code) => t(factorKey(code)))
    .join(' · ');

  return (
    <section className={styles.wrap} aria-label={t('conclusion.banner.title')}>
      <div className={styles.head}>
        <Badge tone="action" icon={<LightbulbIcon weight="bold" />}>
          {t('conclusion.banner.badge')}
        </Badge>
        <p className={styles.title}>
          {t(recommendationKey(recommendation.code), { vehicle: vehicleLabel })}
        </p>
      </div>

      {factors === '' ? null : (
        <p className={styles.factors}>{t('conclusion.banner.because', { factors })}</p>
      )}

      {/* Рекомендация не ограничивает выбор — сказано текстом, а не кнопкой. */}
      <p className={styles.freedom}>{t('conclusion.banner.freedom')}</p>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('conclusion.banner.howCalculated')}
      </Button>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        labelledBy={MODAL_TITLE_ID}
      >
        <h2 id={MODAL_TITLE_ID} className={styles.modalTitle}>
          {t('conclusion.howCalculated.title')}
        </h2>
        <HowCalculated
          conclusion={conclusion}
          weights={weights}
          algorithmVersion={algorithmVersion}
        />
      </Modal>
    </section>
  );
}

/**
 * Разбор расчёта: компоненты, веса, баллы и основания (§38.1).
 *
 * Неприменимый компонент **показывается** и назван неприменимым: молча убрать
 * строку значило бы выдать «не о чем судить» за «проверили и не подтвердилось».
 */
function HowCalculated({
  conclusion,
  weights,
  algorithmVersion,
}: {
  conclusion: Conclusion | null;
  weights: Record<string, number>;
  algorithmVersion: string;
}) {
  const { t } = useTranslation();

  if (conclusion === null || conclusion.factors.length === 0) {
    return <p className={styles.empty}>{t('conclusion.howCalculated.empty')}</p>;
  }

  return (
    <div className={styles.modal}>
      <p className={styles.modalIntro}>{t('conclusion.howCalculated.intro')}</p>

      <ul className={styles.factorList}>
        {conclusion.factors.map((factor) => (
          <li key={factor.code} className={factor.applicable ? undefined : styles.inapplicable}>
            <span className={styles.factorName}>{t(factorKey(factor.code))}</span>
            <span className={styles.factorValue}>
              {factor.applicable && factor.value !== null ? (
                <>
                  <span className="tabular">{Math.round(factor.value)}</span>
                  {factor.weight === null ? null : (
                    <span className={styles.weight}>
                      {' '}
                      {t('conclusion.howCalculated.weight', {
                        value: Math.round(factor.weight),
                        base: weights[factor.code] ?? factor.base_weight,
                      })}
                    </span>
                  )}
                </>
              ) : (
                <span className={styles.empty}>{t('conclusion.howCalculated.inapplicable')}</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {conclusion.total === null ? null : (
        <p className={styles.total}>
          {t('conclusion.howCalculated.total', { value: Math.round(conclusion.total) })}
        </p>
      )}

      {/* Балл ранжирования — не оценка автомобиля и не сумма двух оценок. */}
      <p className={styles.totalNote}>{t('conclusion.howCalculated.totalNote')}</p>
      <p className={styles.version}>
        {t('conclusion.howCalculated.version', { version: algorithmVersion })}
      </p>
    </div>
  );
}
